// Copyright (c) 2025 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { CardHeader } from '@/components/card-header';
import { ConditionalTooltip } from '@/components/conditional-tooltip';
import {
  ArrowForward,
  CheckCircle,
  OfflineBolt,
  WatchLaterOutlined,
} from '@/constants/hardfin-standard-icons';
import { useDisclosure } from '@/hooks/use-disclosure';
import { Project } from '@/project/api/types';
import { useUpdateProject } from '@/project/hooks/use-update-project';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Paper,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';

interface ProjectWorkflowDisplayProps {
  /**
   * The project containing workflow information
   */
  project: Pick<Project, 'activeWorkflow' | 'key'>;

  /**
   * Query key to invalidate after successful update
   */
  queryKey?: string[];
}

function ProjectWorkflowDisplayContent(props: ProjectWorkflowDisplayProps) {
  const { activeWorkflow } = props.project;
  const theme = useTheme();
  const confirmationDialog = useDisclosure();
  const [confirmationDialogData, setConfirmationDialogData] = useState<{
    targetStage: { id: string; name: string } | null;
    currentStage: { id: string; name: string } | null;
    direction: 'forward' | 'backward';
    stageCount: number;
  }>({
    targetStage: null,
    currentStage: null,
    direction: 'forward',
    stageCount: 0,
  });

  const queryClient = useQueryClient();
  const { mutate: updateProject, isPending } = useUpdateProject(
    props.project.key,
    {
      onSuccess: () => {
        if (props.queryKey) {
          queryClient.invalidateQueries({ queryKey: props.queryKey });
        }
        // Let the dialog close naturally and clear all state after closing
        handleCloseDialog();
      },
    }
  );

  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Extract stage data before early return to avoid conditional hooks
  const stages = useMemo(
    () => activeWorkflow?.stages || [],
    [activeWorkflow?.stages]
  );
  const activeStageIndex = stages.findIndex((stage) => stage.active);
  const currentStage = stages[activeStageIndex];

  // Auto-scroll to center the active stage
  useEffect(() => {
    if (
      !activeWorkflow ||
      !scrollContainerRef.current ||
      activeStageIndex === -1
    )
      return;

    const container = scrollContainerRef.current;
    const activeStageElement = container.children[
      activeStageIndex
    ] as HTMLElement;

    if (!activeStageElement) return;

    const containerWidth = container.clientWidth;
    const containerScrollWidth = container.scrollWidth;
    const stageWidth = activeStageElement.offsetWidth;
    const stageLeft = activeStageElement.offsetLeft;

    // Calculate the center position for the active stage
    const stageCenterPosition = stageLeft + stageWidth / 2;
    const desiredScrollLeft = stageCenterPosition - containerWidth / 2;

    // Ensure scroll position is within bounds
    const maxScrollLeft = containerScrollWidth - containerWidth;
    const finalScrollLeft = Math.max(
      0,
      Math.min(desiredScrollLeft, maxScrollLeft)
    );

    container.scrollLeft = finalScrollLeft;
  }, [activeWorkflow, activeStageIndex, stages]);

  if (!activeWorkflow) {
    return null;
  }

  const handleStageClick = (
    targetStage: (typeof stages)[0],
    targetIndex: number
  ) => {
    if (targetIndex === activeStageIndex) return; // Don't allow clicking current stage

    const direction = targetIndex > activeStageIndex ? 'forward' : 'backward';
    const stageCount = Math.abs(targetIndex - activeStageIndex);

    setConfirmationDialogData({
      targetStage,
      currentStage,
      direction,
      stageCount,
    });
    confirmationDialog.open();
  };

  const handleConfirmMove = () => {
    if (confirmationDialogData.targetStage && activeWorkflow) {
      updateProject({
        workflowId: activeWorkflow.id,
        workflowStageId: confirmationDialogData.targetStage.id,
      });
    }
  };

  const handleCloseDialog = () => {
    confirmationDialog.close();
  };

  return (
    <Paper variant="outlined">
      <CardHeader
        title="Project workflow"
        sx={{
          '& .MuiTypography-root': {
            lineHeight: '2.5rem',
          },
          paddingX: 2,
          paddingY: 1,
        }}
      />
      <Divider />
      <Stack padding={2} paddingTop={3} sx={{ minWidth: 0 }}>
        <Stack
          ref={scrollContainerRef}
          direction="row"
          alignItems="center"
          spacing={0}
          sx={{
            overflowX: 'auto',
            pb: 1,
            width: '100%',
            scrollBehavior: 'smooth',
            minWidth: 0,
            justifyContent: 'safe center',
            '& > *': {
              flexShrink: 0,
            },
          }}
        >
          {stages.map((stage, index) => {
            const isCompleted = index < activeStageIndex;
            const isActive = stage.active;
            const isClickable = !isActive;

            const status = isActive
              ? 'current'
              : isCompleted
                ? 'completed'
                : 'future';

            // Calculate if text needs ellipsis
            // Available text width ≈ 200px
            // MUI subtitle1 is ~16px font, roughly 10px per character
            const maxVisibleChars = Math.floor(200 / 10);
            const needsTooltip = stage.name.length > maxVisibleChars;

            return (
              <Stack
                key={stage.id}
                direction="row"
                alignItems="center"
                spacing={0}
              >
                <ConditionalTooltip
                  tooltipText={needsTooltip ? stage.name : undefined}
                  arrow
                >
                  <Paper
                    component={isClickable ? Button : 'div'}
                    onClick={
                      isClickable
                        ? () => handleStageClick(stage, index)
                        : undefined
                    }
                    variant={status === 'current' ? 'elevation' : 'outlined'}
                    sx={{
                      padding: 2.5,
                      minWidth: {
                        xs: '5rem',
                        sm: '7rem',
                        md: '9rem',
                        lg: '12rem',
                      },
                      maxWidth: '15rem',
                      textAlign: 'center',
                      cursor: isClickable ? 'pointer' : 'default',
                      flexShrink: 0,
                      backgroundColor:
                        status === 'current'
                          ? theme.palette.primary.main
                          : status === 'completed'
                            ? theme.palette.success.light
                            : theme.palette.background.paper,
                      color:
                        status === 'current'
                          ? theme.palette.primary.contrastText
                          : status === 'completed'
                            ? theme.palette.success.contrastText
                            : theme.palette.text.primary,
                      '&:hover':
                        isClickable && status === 'completed'
                          ? {
                              color: theme.palette.text.primary,
                            }
                          : {},
                      textTransform: 'none',
                    }}
                  >
                    <Stack
                      direction="row"
                      alignItems="center"
                      spacing={1}
                      justifyContent="center"
                      sx={{ minWidth: 0, width: '100%' }}
                    >
                      {status === 'completed' && (
                        <CheckCircle fontSize="medium" />
                      )}
                      {status === 'current' && (
                        <OfflineBolt fontSize="medium" />
                      )}
                      {status === 'future' && (
                        <WatchLaterOutlined fontSize="medium" />
                      )}
                      <Typography
                        variant="subtitle1"
                        sx={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          flex: '1 1 0',
                          minWidth: 0,
                        }}
                      >
                        {stage.name}
                      </Typography>
                    </Stack>
                  </Paper>
                </ConditionalTooltip>

                {index < stages.length - 1 && (
                  <Stack
                    sx={{
                      width: '3rem',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <ArrowForward color="action" fontSize="medium" />
                  </Stack>
                )}
              </Stack>
            );
          })}
        </Stack>
      </Stack>

      <Dialog
        open={confirmationDialog.isOpen}
        onClose={handleCloseDialog}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Change project stage</DialogTitle>
        <Divider />
        <DialogContent>
          <Typography>
            {confirmationDialogData.stageCount === 1
              ? `Are you sure you want to move this project ${confirmationDialogData.direction} from `
              : `Are you sure you want to jump this project ${confirmationDialogData.direction} ${confirmationDialogData.stageCount} stages from `}
            <strong>{confirmationDialogData.currentStage?.name}</strong> to{' '}
            <strong>{confirmationDialogData.targetStage?.name}</strong>?
          </Typography>
        </DialogContent>
        <Divider />
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={isPending}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirmMove}
            disabled={isPending}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}

/**
 * Displays the project workflow as a horizontal progression of stages
 */
export function ProjectWorkflowDisplay(props: ProjectWorkflowDisplayProps) {
  return <ProjectWorkflowDisplayContent {...props} />;
}
